import { useContext } from 'react';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { Feature } from 'src/WebApiController';
import { useContextSelector } from 'use-context-selector';

import {
  catalogMetricsContextWithSelector,
  catalogMetricsEmptyContextWithSelector,
  createCatalogMetricsContextV2,
  createCatalogMetricsEmptyContext,
  ICatalogMetricsContext,
} from './CatalogMetrics.type';

const useCatalogMetricsContextValueSelector = <
  TMetrics extends { currency: string | null },
  T,
>(
  selector: (context: ICatalogMetricsContext<TMetrics>) => T
): T => {
  const hasCatalogDataContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const isConsignorMasterView = useUserHasConsignorMasterUI();

  const contextValue = useContextSelector(
    isConsignorMasterView
      ? catalogMetricsEmptyContextWithSelector<TMetrics>()
      : catalogMetricsContextWithSelector<TMetrics>(),
    selector
  );
  const context = useContext(
    isConsignorMasterView
      ? createCatalogMetricsEmptyContext<TMetrics>()
      : createCatalogMetricsContextV2<TMetrics>()
  );

  return hasCatalogDataContextWithSelectorFeature
    ? contextValue
    : selector(context);
};

export const useCatalogMetricsContextEventDetailedMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): Record<string, TMetrics> | undefined => {
  return useCatalogMetricsContextValueSelector<
    TMetrics,
    Record<string, TMetrics> | undefined
  >((context) => context.eventDetailedMetrics);
};

export const useCatalogMetricsContextEventMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): Record<string, TMetrics> | undefined => {
  return useCatalogMetricsContextValueSelector<
    TMetrics,
    Record<string, TMetrics> | undefined
  >((context) => context.eventMetrics);
};

export const useCatalogMetricsContextIsLoadingSelector = <
  TMetrics extends { currency: string | null },
>(): boolean => {
  return useCatalogMetricsContextValueSelector<TMetrics, boolean>(
    (context) => context.isLoading
  );
};

export const useCatalogMetricsContextRefreshMetricsSelector = <
  TMetrics extends { currency: string | null },
>(): (() => void) => {
  return useCatalogMetricsContextValueSelector<TMetrics, () => void>(
    (context) => context.refreshMetrics
  );
};
